<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
      <p class="text-sm font-semibold text-gray-900 dark:text-white">Visitors by Country</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Geographic distribution of visitors</p>
    </div>

    <!-- Skeleton -->
    <div v-if="loading && !geoData" class="animate-pulse p-4 h-64 flex items-center justify-center">
      <div class="w-full h-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-16 text-center px-4">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253"/>
        </svg>
      </div>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">No visitor data yet</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Geographic data will appear once you receive visitors.</p>
    </div>

    <!-- Map -->
    <div v-else ref="mapContainer" class="w-full h-72"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useAnalyticsTrafficStore } from 'stores/analyticsTraffic.js'
import topology from '../../../assets/maps/world.topo.json'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsTrafficStore()

const mapContainer = ref(null)
const loading  = computed(() => store.geoLoading)
const geoData  = computed(() => store.geoData[props.duration] ?? null)
const isEmpty  = computed(() => Array.isArray(geoData.value) && geoData.value.length === 0)

let chart = null

function renderMap() {
  if (!geoData.value?.length || !mapContainer.value) return
  if (chart) { chart.destroy(); chart = null }
  chart = Highcharts.mapChart(mapContainer.value, {
    chart: { map: topology, backgroundColor: 'transparent' },
    title: { text: null },
    credits: { enabled: false },
    colorAxis: { min: 1, max: 1000, type: 'logarithmic', minColor: '#dbeafe', maxColor: '#2563eb' },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '12px' },
      pointFormat: '<b>{point.name}</b><br>{point.value} visitors',
    },
    series: [{
      data: geoData.value,
      joinBy: ['iso-a3', 'code3'],
      name: 'Visitors',
    }],
  })
}

watch(geoData, (val) => {
  if (val?.length) nextTick(renderMap)
})

onMounted(() => {
  if (geoData.value?.length) renderMap()
})

onUnmounted(() => {
  if (chart) { chart.destroy(); chart = null }
})
</script>
